import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { RoutesPaths } from "../../Router/constant";
import AuthLayout from "./AuthLayout";
import { BackIcon, InfoIcon, LockIcon, ShieldLockIcon } from "../../Components/Icons";

const CODE_LENGTH = 6;
const EXPIRY_SECONDS = 5 * 60;

const formatTime = (totalSeconds) => {
  const minutes = String(Math.floor(totalSeconds / 60)).padStart(2, "0");
  const seconds = String(totalSeconds % 60).padStart(2, "0");
  return `${minutes}:${seconds}`;
};

export default function VerifyOtp() {
  const navigate = useNavigate();
  const { state } = useLocation();

  // Set by the login screen; the placeholder keeps the layout intact on reload.
  const recipient = state?.username || "john.doe@dgi.gov.ao";

  const [digits, setDigits] = useState(Array(CODE_LENGTH).fill(""));
  const [secondsLeft, setSecondsLeft] = useState(EXPIRY_SECONDS);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const inputsRef = useRef([]);

  const code = digits.join("");
  const expired = secondsLeft === 0;

  useEffect(() => {
    inputsRef.current[0]?.focus();
  }, []);

  useEffect(() => {
    if (secondsLeft === 0) return undefined;
    const timer = setInterval(() => {
      setSecondsLeft((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);
    return () => clearInterval(timer);
  }, [secondsLeft]);

  const setDigitAt = (index, value) => {
    setDigits((prev) => {
      const next = [...prev];
      next[index] = value;
      return next;
    });
  };

  const handleChange = (index) => (event) => {
    const value = event.target.value.replace(/\D/g, "");
    if (!value) {
      setDigitAt(index, "");
      return;
    }

    setError("");

    // Typing over a filled box, or a browser autofilling the whole code.
    if (value.length > 1) {
      const chars = value.slice(0, CODE_LENGTH - index).split("");
      setDigits((prev) => {
        const next = [...prev];
        chars.forEach((char, offset) => {
          next[index + offset] = char;
        });
        return next;
      });
      inputsRef.current[Math.min(index + chars.length, CODE_LENGTH - 1)]?.focus();
      return;
    }

    setDigitAt(index, value);
    if (index < CODE_LENGTH - 1) inputsRef.current[index + 1]?.focus();
  };

  const handleKeyDown = (index) => (event) => {
    if (event.key === "Backspace" && !digits[index] && index > 0) {
      event.preventDefault();
      setDigitAt(index - 1, "");
      inputsRef.current[index - 1]?.focus();
    }
    if (event.key === "ArrowLeft" && index > 0) {
      inputsRef.current[index - 1]?.focus();
    }
    if (event.key === "ArrowRight" && index < CODE_LENGTH - 1) {
      inputsRef.current[index + 1]?.focus();
    }
  };

  const handlePaste = (event) => {
    const pasted = event.clipboardData
      .getData("text")
      .replace(/\D/g, "")
      .slice(0, CODE_LENGTH);
    if (!pasted) return;

    event.preventDefault();
    setError("");
    const next = Array(CODE_LENGTH).fill("");
    pasted.split("").forEach((char, index) => {
      next[index] = char;
    });
    setDigits(next);
    inputsRef.current[Math.min(pasted.length, CODE_LENGTH - 1)]?.focus();
  };

  const handleSubmit = (event) => {
    event.preventDefault();

    if (code.length < CODE_LENGTH) {
      setError("Enter the complete 6-digit code");
      return;
    }
    if (expired) {
      setError("This code has expired. Request a new one.");
      return;
    }

    setSubmitting(true);
    // TODO: replace with the OTP verification API call.
    navigate(RoutesPaths.DASHBOARD);
  };

  const handleResend = () => {
    // TODO: call the resend-code endpoint.
    setDigits(Array(CODE_LENGTH).fill(""));
    setSecondsLeft(EXPIRY_SECONDS);
    setError("");
    inputsRef.current[0]?.focus();
  };

  return (
    <AuthLayout>
      <div className="auth-card">
        <div className="auth-card__logo auth-card__logo--icon">
          <ShieldLockIcon size={40} color="#1b4fa8" />
        </div>

        <h2 className="auth-card__title">Verify Your Identity</h2>
        <p className="auth-card__subtitle auth-card__subtitle--tight">
          We&apos;ve sent a 6-digit verification code to
        </p>
        <p className="auth-otp__recipient">{recipient}</p>

        <hr className="auth-card__rule" />

        <form onSubmit={handleSubmit} noValidate>
          <span className="auth-field__label" id="otp-label">
            Enter 6-digit code
          </span>

          <div
            className="auth-otp__boxes"
            role="group"
            aria-labelledby="otp-label"
            onPaste={handlePaste}
          >
            {digits.map((digit, index) => (
              <input
                // eslint-disable-next-line react/no-array-index-key
                key={index}
                ref={(element) => {
                  inputsRef.current[index] = element;
                }}
                type="text"
                inputMode="numeric"
                autoComplete={index === 0 ? "one-time-code" : "off"}
                maxLength={CODE_LENGTH}
                className={`auth-otp__box${
                  error ? " auth-otp__box--invalid" : ""
                }`}
                aria-label={`Digit ${index + 1}`}
                value={digit}
                onChange={handleChange(index)}
                onKeyDown={handleKeyDown(index)}
                onFocus={(event) => event.target.select()}
              />
            ))}
          </div>

          <p className="auth-otp__expiry">
            {expired ? (
              <span className="auth-otp__expired">Code expired</span>
            ) : (
              <>
                Code expires in{" "}
                <span className="auth-otp__timer">
                  {formatTime(secondsLeft)}
                </span>
              </>
            )}
          </p>

          {error && <p className="auth-otp__error">{error}</p>}

          <div className="auth-note">
            <InfoIcon size={20} color="#1b4fa8" />
            <span>
              For your security, this extra step helps us ensure that it&apos;s
              really you signing in.
            </span>
          </div>

          <button type="submit" className="auth-submit" disabled={submitting}>
            <LockIcon size={17} />
            {submitting ? "Verifying…" : "Verify & Continue"}
          </button>
        </form>

        <button
          type="button"
          className="auth-sso auth-sso--secondary"
          onClick={() => navigate(RoutesPaths.LOGIN)}
        >
          <BackIcon size={18} />
          Back to Login
        </button>

        <p className="auth-otp__resend">
          Didn&apos;t receive the code?{" "}
          <button type="button" className="auth-link" onClick={handleResend}>
            Resend code
          </button>
        </p>
      </div>
    </AuthLayout>
  );
}
